import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import * as Sentry from "@sentry/browser";
import { makeAutoObservable } from "mobx";

import type { ResolvedRequestOptions } from "./generated/client/types.gen";

import {
  apiGetAuthLogout,
  apiGetAuthRefresh,
  apiGetInfo,
  apiGetUsersMe,
  APIInfoResponse,
  apiPostAuthLogin,
  apiDeleteMfaTotp,
  apiDeleteMfaWebauthnCredentialUuid,
  apiPostMfaBackupCodesRegenerate,
  apiPostMfaChallengeVerify,
  apiPostMfaChallengeWebauthnOptions,
  apiPostMfaChallengeWebauthnVerify,
  apiPostMfaEnrollTotpConfirm,
  apiPostMfaEnrollTotpStart,
  apiPostMfaEnrollWebauthnOptions,
  apiPostMfaEnrollWebauthnVerify,
  apiPutPatientsPatientIdApproveConsentPaper,
  apiPutPatientsPatientIdApproveConsentTelemonitoring,
  apiPutPatientsPatientIdRevokeConsentPaper,
  apiPutPatientsPatientIdRevokeConsentTelemonitoring,
  client,
  DefaultMedicationSchemaResponse,
  DefaultPatientResponse,
  ErrorCode,
  FeatureFlagState,
  Level3PatientResponse,
  LoginMFAEnrollmentRequired,
  LoginMFARequired,
  LoginResponse,
  MFAMethod,
  PermissionBit,
  SeerlinqEnvironments,
  UserFeatureFlag,
  UserRole,
} from ".";
import { analytics } from "../app/analytics";
import { location } from "../app/location";
import { apiRoute } from "../config/config";
import { SearchParam } from "../constants/constants";
import {
  hasMedicalAccess,
  hasMonitoringAccess,
  hasPermission,
} from "../constants/permissions";
import { updateServerTimeOffset } from "../utils/server-time";
import { assert, Deferral } from "../utils/utils";

const sessionLoginTimeKey = "sessionLoginTime";
const sessionLastRefreshTime = "sessionLastRefreshTime";

export class ApiConnector {
  apiRoute: string;
  apiVersion: string;
  loggedIn: boolean;
  username: string | null;
  userUUID: string | null = null;
  permissions: PermissionBit[];
  featureFlags: Partial<Record<UserFeatureFlag, FeatureFlagState>>;
  apiInfo: APIInfoResponse | null;
  canIAddPatients: boolean;
  isProdEnv: boolean;
  canViewWaitingOrAuditRoom: boolean;
  promptPassword = false;

  /** Only needed to break active users down by role in analytics. */
  private userRole: UserRole | null = null;

  private deferralOn428?: Deferral;
  /** Responses whose failure the connector answered — see `RequestError.handled`. */
  private answeredResponses = new WeakSet<Response>();
  private loggingOut = false;
  private refreshPromise?: Promise<boolean>;
  private preClonedRequests = new Map<Request, Request>();
  private requestOptionsMap = new Map<Request, RequestOptionsWithMeta>();

  constructor(apiRoute: string, apiVersion: string = "v1") {
    this.apiRoute = apiRoute;
    this.apiVersion = apiVersion;
    this.loggedIn = false;
    this.username = null;
    this.permissions = [];
    this.featureFlags = {};
    this.apiInfo = null;
    this.canIAddPatients = false;
    this.isProdEnv = false;
    this.canViewWaitingOrAuditRoom = false;
    makeAutoObservable(this);
    console.log("Initialized with route", apiRoute);
  }

  init() {
    client.setConfig({
      baseUrl: apiRoute,
      credentials: "include",
    });

    client.interceptors.request.use(this.interceptRequest);
    client.interceptors.response.use(this.interceptResponse);
    client.interceptors.error.use(this.interceptError);
  }

  destroy() {
    client.interceptors.request.eject(this.interceptRequest);
    client.interceptors.response.eject(this.interceptResponse);
    client.interceptors.error.eject(this.interceptError);
  }

  versionedRoute() {
    return `${this.apiRoute}/api/${this.apiVersion}`;
  }

  requireUserUUID(): string {
    if (this.userUUID == null) {
      throw new Error("userUUID is not set — user is not logged in");
    }
    return this.userUUID;
  }

  /**
   * Whether a feature flag is on for the logged-in user. `GET /users/me` serves
   * the whole registry already merged with the API's code-side defaults, so an
   * absent flag means an API that does not know that flag yet - off, rather
   * than a default this side would have to keep in sync.
   */
  hasFeatureFlag(flag: UserFeatureFlag): boolean {
    return this.featureFlags[flag]?.value === true;
  }

  // headers

  private refreshHeaders() {
    const csrfToken = this.getCSRFToken("csrf_refresh_token");
    return { "X-CSRF-TOKEN": csrfToken };
  }

  private getCSRFToken(token: string): string | null {
    let csrfToken = null;
    const cookies = document.cookie.split(";");
    cookies.forEach((cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name === token) {
        csrfToken = decodeURIComponent(value);
      }
    });
    return csrfToken !== null ? csrfToken : null;
  }

  // auth flow

  /**
   * Log in with a password. Even a `success` may leave something to do before
   * the app opens, so the outcome is returned and the caller decides when to
   * call `completeLogin`.
   */
  async login(username: string, password: string) {
    const { data } = await apiPostAuthLogin({
      body: {
        username: username,
        password: password,
      },
      meta: { rotateOn401: "never" } satisfies RequestMeta,
    });

    return data;
  }

  /**
   * Finish a login once tokens (and their cookies) have been issued — either
   * by `/auth/login` directly, or by an MFA endpoint that completed a
   * challenge.
   */
  completeLogin() {
    // eslint-disable-next-line no-restricted-syntax
    localStorage.setItem(sessionLoginTimeKey, new Date().toISOString());

    console.log("Logged in");
    location.assign(loginRedirectTarget(location.href));
  }

  /**
   * Re-authenticate to satisfy a 428, so the request that triggered the
   * password prompt can be retried.
   *
   * MFA gates a fresh login too, so this doesn't always end in a fresh token —
   * only `success` releases the waiting request, and the outcome is returned so
   * the caller can carry the challenge on.
   */
  async relogin(password: string) {
    assert(this.username, "Username is missing");

    const { data } = await apiPostAuthLogin({
      body: {
        username: this.username,
        password: password,
      },
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });

    if (data.status === "success") {
      this.completeFreshLogin();
    }

    return data;
  }

  /**
   * Release the request that was waiting on the password prompt, now that a
   * fresh token has been issued — by `relogin` directly, or by verifying the
   * MFA challenge it returned.
   */
  completeFreshLogin() {
    this.deferralOn428?.resolve();
    this.deferralOn428 = undefined;
    this.promptPassword = false;
  }

  cancelPasswordPrompt() {
    this.deferralOn428?.reject();
    this.deferralOn428 = undefined;
    this.promptPassword = false;
  }

  // MFA

  /**
   * Begin TOTP enrollment and get the secret to show as a QR code.
   *
   * Pass `challengeId` for a mandatory login-time enrollment; omit it for a
   * voluntary one from account settings. What authenticates the call decides
   * both meta flags: a login-time one is carried by the challenge, so there is
   * no token to rotate and no session to re-authenticate.
   */
  async startTotpEnrollment(challengeId?: string) {
    const bySession = challengeId === undefined;

    const { data } = await apiPostMfaEnrollTotpStart({
      body: bySession ? {} : { challenge_id: challengeId },
      meta: {
        rotateOn401: bySession ? "when-token-invalid" : "never",
        reauthOn428: bySession,
      } satisfies RequestMeta,
    });
    return data;
  }

  /**
   * Confirm TOTP enrollment with the first code from the authenticator app.
   * Completing a login-time enrollment also issues the session cookies, so
   * `completeLogin` can follow once the user has stored their backup codes.
   *
   * A rejected code is a 401 of its own, which is why the session-authenticated
   * path can't simply rotate on every 401: it would retry the confirmation and
   * spend a second attempt on a code that has since rolled over.
   */
  async confirmTotpEnrollment(
    challengeId: string | undefined,
    secret: string,
    code: string,
  ) {
    const bySession = challengeId === undefined;

    const { data } = await apiPostMfaEnrollTotpConfirm({
      body: bySession
        ? { secret: secret, code: code }
        : { challenge_id: challengeId, secret: secret, code: code },
      meta: {
        rotateOn401: bySession ? "when-token-invalid" : "never",
        reauthOn428: bySession,
      } satisfies RequestMeta,
    });
    return data;
  }

  /**
   * Begin passkey enrollment and get the options for the browser's prompt.
   * Pass `challengeId` for a mandatory login-time enrollment; omit it for a
   * voluntary one.
   */
  async startPasskeyEnrollment(challengeId?: string) {
    const bySession = challengeId === undefined;

    const { data } = await apiPostMfaEnrollWebauthnOptions({
      body: bySession ? {} : { challenge_id: challengeId },
      meta: {
        rotateOn401: bySession ? "when-token-invalid" : "never",
        reauthOn428: bySession,
      } satisfies RequestMeta,
    });

    // The backend hands the options over as a JSON string.
    return JSON.parse(data.options) as PublicKeyCredentialCreationOptionsJSON;
  }

  /**
   * Finish passkey enrollment with what the browser's prompt returned. A
   * login-time enrollment also issues the session cookies, so `completeLogin`
   * can follow once the user has stored their backup codes.
   */
  async confirmPasskeyEnrollment(
    challengeId: string | undefined,
    credential: RegistrationResponseJSON,
  ) {
    const bySession = challengeId === undefined;

    const { data } = await apiPostMfaEnrollWebauthnVerify({
      body: {
        challenge_id: challengeId,
        credential_json: JSON.stringify(credential),
      },
      meta: {
        rotateOn401: bySession ? "when-token-invalid" : "never",
        reauthOn428: bySession,
      } satisfies RequestMeta,
    });
    return data;
  }

  /**
   * Verify a code against a login-time challenge. Succeeding issues the
   * session cookies, so `completeLogin` can follow.
   *
   * Unlike enrollment, a rejected code leaves the challenge usable — a plain
   * 401 for the caller to handle. The challenge is only discarded once the
   * attempt budget runs out, which comes back as a 403.
   */
  async verifyChallenge(
    challengeId: string,
    method: ChallengeVerifyMethod,
    code: string,
  ) {
    const { data } = await apiPostMfaChallengeVerify({
      body: { challenge_id: challengeId, method: method, code: code },
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });
    return data;
  }

  /**
   * Begin verifying a login-time challenge with a passkey, and get the options
   * for the browser's prompt.
   */
  async startPasskeyVerification(challengeId: string) {
    const { data } = await apiPostMfaChallengeWebauthnOptions({
      body: { challenge_id: challengeId },
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });

    // The backend hands the options over as a JSON string.
    return JSON.parse(data.options) as PublicKeyCredentialRequestOptionsJSON;
  }

  /**
   * Finish verifying a login-time challenge with what the browser's prompt
   * returned. Succeeding issues the session cookies, so `completeLogin` can
   * follow.
   */
  async verifyPasskey(
    challengeId: string,
    credential: AuthenticationResponseJSON,
  ) {
    const { data } = await apiPostMfaChallengeWebauthnVerify({
      body: {
        challenge_id: challengeId,
        credential_json: JSON.stringify(credential),
      },
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });
    return data;
  }

  /**
   * Issue a fresh set of backup codes, invalidating the old ones. Gated on a
   * fresh token, so this can raise the password prompt.
   */
  async regenerateBackupCodes() {
    const { data } = await apiPostMfaBackupCodesRegenerate();
    return data;
  }

  /**
   * Drop the enrolled authenticator app, and with it the backup codes — they
   * recover a method, so they don't outlive the last one. Refused for an
   * account two-factor is required for unless another method remains. Gated on
   * a fresh token, so this can raise the password prompt.
   */
  async removeTotp() {
    await apiDeleteMfaTotp();
  }

  /**
   * Remove one of the user's passkeys. Refused for an account two-factor is
   * required for if it is the last method left. Gated on a fresh token, so this
   * can raise the password prompt.
   */
  async removePasskey(credentialUuid: string) {
    await apiDeleteMfaWebauthnCredentialUuid({
      path: { credential_uuid: credentialUuid },
    });
  }

  private checkWaitingOrAuditRoomAccess() {
    return hasPermission(this.permissions, PermissionBit.VIEW_WAITING_ROOM);
  }

  private checkCanAddPatients(monitoringActive: boolean) {
    // What `POST /patients` itself requires
    if (
      !hasPermission(this.permissions, PermissionBit.CREATE_PATIENTS) ||
      !hasPermission(this.permissions, PermissionBit.WRITE)
    )
      return false;
    // Monitoring-level users can always add patients
    if (hasMonitoringAccess(this.permissions)) return true;
    // Physicians with active monitoring can add patients
    return hasMedicalAccess(this.permissions) && monitoringActive;
  }

  /** Check whether there is a valid session (without triggering logout on failure). */
  async hasSession(): Promise<boolean> {
    const { response } = await apiGetUsersMe({
      throwOnError: false,
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });
    return response.ok;
  }

  async assumeLoggedIn() {
    try {
      await Promise.all([this.fetchMyInfo(), this.fetchApiInfo()]);
    } catch (error) {
      // The session's user doesn't exist here — typically after switching to
      // an environment with a different database. Nothing works without a
      // user, so treat it like an expired session.
      if (apiErrorCode(error) === ErrorCode.USER_NOT_FOUND) {
        void this.logout(true);
        return;
      }
      throw error;
    }

    // Both halves are needed: the user id to identify by, and the API
    // environment that decides whether we track at all. Not awaited — it
    // fetches the analytics chunk, and nothing here depends on it.
    void analytics.init({
      userUUID: this.requireUserUUID(),
      isProdEnv: this.isProdEnv,
      role: this.userRole,
    });
  }

  private async fetchMyInfo() {
    const { data } = await apiGetUsersMe();
    this.username = data.username;
    this.userUUID = data.uuid;
    this.userRole = data.role ?? null;
    this.permissions = data.access_policy?.permissions ?? [];
    this.featureFlags = data.feature_flags ?? {};
    this.canViewWaitingOrAuditRoom = this.checkWaitingOrAuditRoomAccess();
    this.canIAddPatients = this.checkCanAddPatients(
      data.access_policy?.monitoring_active ?? false,
    );

    this.loggedIn = true;
    Sentry.setUser({ id: data.uuid });
  }

  private async fetchApiInfo() {
    // eslint-disable-next-line no-restricted-syntax -- measuring RTT, not creating a timestamp
    const requestStart = Date.now();
    const info = await apiGetInfo();
    // eslint-disable-next-line no-restricted-syntax -- measuring RTT, not creating a timestamp
    const requestEnd = Date.now();
    this.apiInfo = info.data;
    // The /info spec types `environment` as a plain string, so compare against
    // the enum's string value rather than the enum member.
    this.isProdEnv =
      this.apiInfo.environment === (SeerlinqEnvironments.prod as string);

    // Calculate server time offset for timestamp synchronization
    updateServerTimeOffset(
      this.apiInfo.server_time_utc,
      requestStart,
      requestEnd,
    );
  }

  async logout(returnAfterLogin = false) {
    if (this.loggingOut) {
      return; // Prevent re-entry
    }

    this.loggingOut = true;
    await apiGetAuthLogout({
      throwOnError: false,
      meta: { rotateOn401: "never", reauthOn428: false } satisfies RequestMeta,
    });
    localStorage.removeItem(sessionLoginTimeKey);
    localStorage.removeItem(sessionLastRefreshTime);
    analytics.reset();
    console.log("Logged out");

    const { pathname } = new URL(location.href);
    location.assign(
      "/login" +
        (returnAfterLogin
          ? `?${SearchParam.redirect}=${encodeURIComponent(pathname)}`
          : ""),
    );
  }

  private async tryRefresh() {
    // This trick with storing the Promise prevents multiple simultaneous calls
    if (!this.refreshPromise) {
      const headers = this.refreshHeaders();
      this.refreshPromise = apiGetAuthRefresh({
        headers,
        meta: {
          rotateOn401: "never",
          reauthOn428: false,
        } satisfies RequestMeta,
      })
        .then(() => {
          localStorage.setItem(
            sessionLastRefreshTime,
            new Date().toISOString(), // eslint-disable-line no-restricted-syntax
          );
          return true;
        })
        .catch((error) => {
          if (error instanceof RequestError && error.response?.status === 401) {
            // 401 means the refresh token is probably expired
            this.addRefreshBreadcrumb(error);
            return false;
          } else {
            // Rethrow any other error. This shouldn't log out
            throw error;
          }
        })
        .finally(() => {
          this.refreshPromise = undefined;
        });
    }
    return this.refreshPromise;
  }

  private addRefreshBreadcrumb(error: RequestError) {
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Token refresh failed",
      level: "warning",
      data: {
        status_code: error.response?.status,
        responseBody: error.data,
        url: error.response?.url,
      },
    });
  }

  // API operations

  async revokePaperConsent(patId: number) {
    await apiPutPatientsPatientIdRevokeConsentPaper({
      path: { patient_id: patId },
    });
    location.reload();
  }

  async revokeTeleConsent(patId: number) {
    await apiPutPatientsPatientIdRevokeConsentTelemonitoring({
      path: { patient_id: patId },
    });
    location.reload();
  }

  async approvePaperConsent(patId: number) {
    await apiPutPatientsPatientIdApproveConsentPaper({
      path: { patient_id: patId },
    });
    location.reload();
  }

  async approveTeleConsent(patId: number) {
    await apiPutPatientsPatientIdApproveConsentTelemonitoring({
      path: { patient_id: patId },
    });
    location.reload();
  }

  /**
   * POST and stream the response.
   *
   * `base` defaults to db-api's versioned route. Pass it to reach a
   * service mounted elsewhere — harness serves its routes at its own root,
   * not under `/api/v1` — while keeping the cookie, CSRF header and
   * 401-refresh behaviour every authenticated call needs.
   */
  async streamingFetch(
    path: string,
    body: unknown,
    signal?: AbortSignal,
    base?: string,
  ): Promise<Response> {
    const doFetch = () => {
      const csrfToken = this.getCSRFToken("csrf_access_token");
      return fetch(`${base ?? this.versionedRoute()}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken && { "X-CSRF-TOKEN": csrfToken }),
        },
        credentials: "include",
        body: JSON.stringify(body),
        signal,
      });
    };

    let response = await doFetch();
    if (response.status === 401) {
      const ok = await this.tryRefresh();
      if (!ok) {
        void this.logout(true);
        throw new Error("Authentication failed");
      }
      response = await doFetch();
    }
    return response;
  }

  private attachCSRFToken(request: Request) {
    // Add CSRF token for POST/PUT
    if (
      request.method === "POST" ||
      request.method === "PUT" ||
      request.method === "DELETE"
    ) {
      const token = this.getCSRFToken("csrf_access_token");
      if (token) {
        request.headers.set("X-CSRF-TOKEN", token);
      }
    }
  }

  private interceptRequest = (
    request: Request,
    options: RequestOptionsWithMeta,
  ) => {
    // Prepare a clone of the request in case we need to retry it (we cannot clone it after it has been used anymore)
    this.preClonedRequests.set(request, request.clone());
    this.requestOptionsMap.set(request, options);
    this.attachCSRFToken(request);
    return request;
  };

  private interceptResponse = async (
    response: Response,
    request: Request,
    options: RequestOptionsWithMeta,
  ) => {
    const clonedRequest = this.preClonedRequests.get(request);
    assert(clonedRequest, "Request clone not found");

    this.preClonedRequests.delete(request);
    this.requestOptionsMap.delete(request);

    if (response.status === 401) {
      return this.handle401(clonedRequest, response, options.meta);
    } else if (response.status === 428 && (options.meta?.reauthOn428 ?? true)) {
      return this.handle428(clonedRequest, response);
    } else {
      return response;
    }
  };

  /**
   * Recover from a 401 by rotating the token: refresh once, then retry. Logging
   * out is that recovery failing — so a 401 we don't rotate never logs the user
   * out, it goes back to the caller to make sense of.
   */
  private async handle401(
    request: Request,
    response: Response,
    meta?: RequestMeta,
  ) {
    const rotate = meta?.rotateOn401 ?? "always";

    if (!(await rotates(response, rotate))) {
      return response;
    }

    const refreshSuccess = await this.tryRefresh();

    if (!refreshSuccess) {
      await this.captureLogoutEvent(response, "Refresh failed");
      void this.logout(true);
      this.answeredResponses.add(response);
      return response;
    }

    // Retry fetch if refresh was successful
    response = await this.refetch(request);
    if (response.status === 401 && (await rotates(response, rotate))) {
      await this.captureLogoutEvent(response, "Retry after refresh failed");
      void this.logout(true);
      this.answeredResponses.add(response);
    }

    if (response.status === 428 && (meta?.reauthOn428 ?? true)) {
      return this.handle428(request, response);
    }

    return response;
  }

  private async captureLogoutEvent(response: Response, reason: string) {
    let responseBody: string | null = null;
    try {
      responseBody = await response.clone().text();
    } catch {
      // ignore
    }

    // eslint-disable-next-line no-restricted-syntax
    const now = Date.now();
    const loginTime = localStorage.getItem(sessionLoginTimeKey);
    const lastRefreshTime = localStorage.getItem(sessionLastRefreshTime);

    Sentry.captureMessage("User logged out due to 401", {
      level: "info",
      extra: {
        reason,
        loginTime,
        lastRefreshTime,
        sessionDurationMs: loginTime ? now - Date.parse(loginTime) : null,
        timeSinceLastRefreshMs: lastRefreshTime
          ? now - Date.parse(lastRefreshTime)
          : null,
        responseStatus: response.status,
        responseBody,
        requestUrl: response.url,
      },
    });

    await Sentry.flush(2000);
  }

  private async handle428(request: Request, response: Response) {
    this.promptPassword = true;

    if (!this.deferralOn428) {
      this.deferralOn428 = new Deferral();
    }

    try {
      await this.deferralOn428.promise;
    } catch {
      // Prompt cancelled => return the original response
      this.answeredResponses.add(response);
      return response;
    }

    // Retry fetch
    return this.refetch(request);
  }

  private refetch(request: Request) {
    request = request.clone();
    this.attachCSRFToken(request);
    return fetch(request);
  }

  private interceptError = (
    error: unknown,
    response: Response | undefined,
    request: Request,
  ) => {
    this.requestOptionsMap.delete(request);

    // No response means fetch itself threw (offline, DNS, CORS, abort): pass
    // that error on as-is so the global handler can recognise it.
    if (!response) {
      return error;
    }

    return new RequestError(
      error,
      response,
      request,
      this.answeredResponses.has(response),
    );
  };
}

export class RequestError extends Error {
  name = "RequestError";

  constructor(
    readonly data: unknown,
    readonly response: Response,
    readonly request: Request,
    /**
     * Whether the connector already answered this failure — logged the user out
     * over a 401 it couldn't recover, or dropped a request whose password
     * prompt was dismissed. The global handler stays quiet for those; every
     * other failure is one a caller was meant to explain and didn't.
     */
    readonly handled = false,
  ) {
    super(`Status ${response.status} from ${request.method} ${request.url}`);
  }
}

/**
 * Where to send the browser after logging in, taken from the `redirect` param
 * of the page it happened on. Anything but a path on this origin is dropped:
 * the login page is where an open redirect is worth phishing, and `//host` and
 * `/\host` pass for paths until the browser resolves them.
 */
export function loginRedirectTarget(href: string): string {
  const redirect = new URL(href).searchParams.get(SearchParam.redirect);

  if (!redirect) {
    return "/";
  }

  if (!/^\/(?![/\\])[^\s]*$/.test(redirect)) {
    console.error("Invalid redirect param");
    return "/";
  }

  return redirect;
}

/**
 * Reads the `error_code` out of an API error body. The backend returns
 * `{detail, error_category, error_code}` on handled errors, but error
 * responses aren't in the OpenAPI spec, so the generated types can't narrow
 * this for us.
 */
export function apiErrorCode(error: unknown): ErrorCode | null {
  if (!(error instanceof RequestError)) {
    return null;
  }

  const code =
    error.data && typeof error.data === "object" && "error_code" in error.data
      ? error.data.error_code
      : null;

  return typeof code === "string" &&
    (Object.values(ErrorCode) as string[]).includes(code)
    ? (code as ErrorCode)
    : null;
}

/**
 * Whether a 401 is one the request wants recovered by a token rotation.
 *
 * Telling the two apart rests on the backend answering a handled error with an
 * `error_code` and a JWT failure with a bare `detail`. An unreadable body is
 * treated as a token failure: rotating when we shouldn't have costs a refresh,
 * not rotating when we should have logs the user out.
 */
async function rotates(response: Response, rotate: RotateOn401) {
  switch (rotate) {
    case "always":
      return true;

    case "never":
      return false;

    case "when-token-invalid":
      return !(await isHandledApiError(response));
  }
}

async function isHandledApiError(response: Response) {
  try {
    const data: unknown = await response.clone().json();
    return data !== null && typeof data === "object" && "error_code" in data;
  } catch {
    return false;
  }
}

/** This is the type we get from the `/patients` endpoint when we use `load_type: basic`. */
export type PatientDto = Level3PatientResponse | DefaultPatientResponse;

export type MedicationDto = DefaultMedicationSchemaResponse;

interface RequestOptionsWithMeta extends ResolvedRequestOptions {
  meta?: RequestMeta;
}

/** What a login answers: a session, or the second factor it wants first. */
export type LoginOutcome =
  | LoginResponse
  | LoginMFARequired
  | LoginMFAEnrollmentRequired;

/**
 * The methods `/mfa/challenge/verify` takes. `passkey` is absent because its
 * assertion is a separate ceremony, not a code.
 */
export type ChallengeVerifyMethod = MFAMethod.totp | MFAMethod.backup_code;

interface RequestMeta {
  /**
   * When a 401 should be recovered by rotating the token — refresh, then
   * retry. The default assumes every 401 is an expired token: being wrong that
   * way costs a refresh, being wrong the other way logs the user out.
   *
   * `when-token-invalid` is for endpoints that answer 401 with a meaning of
   * their own. Those come back carrying an `error_code` and are handed to the
   * caller untouched; a token failure carries only a `detail`, and still
   * rotates.
   */
  rotateOn401?: RotateOn401;
  /**
   * Whether a 428 raises the password prompt and replays the request once a
   * fresh token has been issued. Off where there is no session to re-authenticate,
   * or where the caller carries the re-authentication itself.
   */
  reauthOn428?: boolean;
}

type RotateOn401 = "always" | "never" | "when-token-invalid";
